import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import MaterialDropdown from '../materialDropdown/MaterialDropdown';
import PlayButton from '../playButton/PlayButton';
import './VoiceSelection.css';

interface VoiceSelectionProps {
  languageCode?: string;
  text?: string;
  disabled?: boolean;
  onVoiceChange?: (voiceId: string) => void;
  onSpeedChange?: (speed: number) => void;
}

const VoiceSelection: React.FC<VoiceSelectionProps> = ({
  languageCode,
  text,
  disabled = false,
  onVoiceChange,
  onSpeedChange,
}) => {
  const [voices, setVoices] = useState<{ [key: string]: string }>({});
  const [selectedVoice, setSelectedVoice] = useState<string>('random');
  const [voiceId, setVoiceId] = useState<string>('');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState<boolean>(false);
  const [audioSpeed, setAudioSpeed] = useState<number>(() => {
    const savedSpeed = localStorage.getItem('audioSpeed');
    return savedSpeed ? Number(savedSpeed) : 0.9;
  });

  const fetchVoices = useCallback(async () => {
    if (!languageCode) {
      console.error('No language code found');
      return;
    }

    try {
      const response = await axios.get(
        `/api/audio/speech/voices/${languageCode}`
      );
      const data = response.data;
      const updatedVoices = { Random: 'random', ...data };
      setVoices(updatedVoices);
      setVoiceId('random');
    } catch (error) {
      console.error('Error fetching voices:', error);
    }
  }, [languageCode]);

  useEffect(() => {
    fetchVoices();
  }, [fetchVoices]);

  useEffect(() => {
    if (voiceId) {
      onVoiceChange?.(voiceId);
    }
  }, [voiceId, onVoiceChange]);

  const handleVoiceChange = async (newVoice: string) => {
    setSelectedVoice(newVoice);
    setAudioUrl(null);
    setVoiceId(newVoice);
    onVoiceChange?.(newVoice);
  };

  const handleSpeedChange = (newSpeed: string) => {
    const speed = Number(newSpeed);
    setAudioSpeed(speed);
    localStorage.setItem('audioSpeed', speed.toString());
    setAudioUrl(null);
    onSpeedChange?.(speed);
  };

  const handlePlay = async () => {
    if (!text || !selectedVoice) {
      throw new Error('No text or voice selected');
    }

    if (!audioUrl) {
      setAudioLoading(true);
      try {
        const response = await axios.post('/api/audio/speech', {
          text,
          voice_id: voiceId,
          language_code: languageCode,
          speed: audioSpeed,
        });
        setAudioUrl(response.data);
      } catch (error) {
        console.error('Error generating speech:', error);
        toast.error('Failed to generate speech');
        throw error;
      } finally {
        setAudioLoading(false);
      }
    }
  };

  return (
    <div className="voice-dropdowns">
      <MaterialDropdown
        id="voice-dropdown"
        label="Voice"
        value={selectedVoice}
        onChange={handleVoiceChange}
        options={Object.entries(voices).map(([key, value]) => ({
          value: value as string,
          label: key,
        }))}
        disabled={disabled}
        className="voice-dropdown"
        fullWidth={false}
      />

      <MaterialDropdown
        id="speed-dropdown"
        label="Speed"
        value={audioSpeed.toString()}
        onChange={handleSpeedChange}
        options={[
          { value: '0.5', label: '0.5x' },
          { value: '0.6', label: '0.6x' },
          { value: '0.7', label: '0.7x' },
          { value: '0.8', label: '0.8x' },
          { value: '0.9', label: '0.9x' },
          { value: '1.0', label: '1.0x' },
        ]}
        disabled={disabled}
        className="speed-dropdown"
        fullWidth={false}
      />

      <PlayButton
        src={audioUrl}
        onPlay={handlePlay}
        disabled={disabled || !text}
        isLoading={audioLoading}
        autoPlay={true}
      />
    </div>
  );
};

export default VoiceSelection;
